import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Search, SlidersHorizontal, X, ChevronLeft, ChevronRight, Plus, Sparkles, CalendarClock, Crown } from "lucide-react";
import ReleaseCard from "../components/ReleaseCard";
import CatalogFilters from "../components/CatalogFilters";
import AdminReleaseForm from "../components/AdminReleaseForm";
import QuickScanDialog from "../components/QuickScanDialog";
import BarcodeScanDialog from "../components/BarcodeScanDialog";
import SuggestReleaseDialog from "../components/SuggestReleaseDialog";
import AddCustomItemDialog from "../components/AddCustomItemDialog";
import { toast } from "sonner";

function CatalogSearchBar({ search, sort, hasActiveFilters, showFilters, onFilterChange, onToggleFilters }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          data-testid="catalog-search"
          value={search}
          onChange={(e) => onFilterChange("search", e.target.value)}
          placeholder="Search releases, chassis, variants..."
          className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-rose-500"
        />
      </div>
      <div className="flex gap-2">
        <Select value={sort} onValueChange={(v) => onFilterChange("sort", v)}>
          <SelectTrigger data-testid="sort-select" className="w-[160px] bg-zinc-900 border-zinc-800 text-zinc-300"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="just_released">Just Released</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
            <SelectItem value="alpha">A-Z</SelectItem>
            <SelectItem value="value_high">Highest Value</SelectItem>
            <SelectItem value="value_low">Lowest Value</SelectItem>
            <SelectItem value="chase_first">Chase First</SelectItem>
          </SelectContent>
        </Select>
        <Link
          to="/preorders"
          data-testid="preorders-nav-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200 hover:bg-fuchsia-500/20 hover:border-fuchsia-500/60 transition whitespace-nowrap"
        >
          <CalendarClock className="w-4 h-4" /> Future Releases
        </Link>
        <Link
          to="/brands"
          data-testid="brands-nav-btn"
          className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border border-amber-500/30 bg-amber-500/5 text-amber-200 hover:bg-amber-500/15 hover:border-amber-500/50 transition whitespace-nowrap"
          title="Public brand directory"
        >
          <Crown className="w-4 h-4" /> Brand Index
        </Link>
        <Button
          data-testid="toggle-filters"
          variant="outline"
          onClick={onToggleFilters}
          className={`bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800 ${hasActiveFilters ? 'border-rose-500/50 text-rose-400' : ''}`}
        >
          <SlidersHorizontal className="w-4 h-4 mr-1" /> Filters {hasActiveFilters && <Badge className="ml-1 bg-rose-600 text-white text-[10px] px-1 py-0">!</Badge>}
        </Button>
      </div>
    </div>
  );
}

function CatalogPagination({ page, pages, onFilterChange }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 pt-4">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onFilterChange("page", String(page - 1))} className="bg-zinc-900 border-zinc-800 text-zinc-400">
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <span className="text-sm text-zinc-400 font-mono px-3">{page} / {pages}</span>
      <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onFilterChange("page", String(page + 1))} className="bg-zinc-900 border-zinc-800 text-zinc-400">
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

const SKELETON_IDS = Array.from({ length: 12 }, (_, i) => `catalog-skel-${i}`);

function CatalogSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {SKELETON_IDS.map((id) => (
        <div key={id} className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl overflow-hidden animate-pulse">
          <div className="aspect-square bg-zinc-800" />
          <div className="p-4 space-y-2">
            <div className="h-3 bg-zinc-800 rounded w-1/3" />
            <div className="h-4 bg-zinc-800 rounded" />
            <div className="h-3 bg-zinc-800 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

const TIER_DOT = {
  "hyper-premium": "bg-amber-400",
  premium: "bg-fuchsia-400",
  mainline: "bg-zinc-400",
};

function BrandTab({ active, onClick, label, count, tier, testId }) {
  const dotColor = TIER_DOT[tier];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap transition ${
        active
          ? "bg-rose-500/20 border-rose-400/60 text-rose-100 shadow-[0_0_14px_rgba(244,63,94,0.25)]"
          : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-rose-500/30 hover:text-rose-200"
      }`}
    >
      {dotColor && <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />}
      <span>{label}</span>
      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
        active ? "bg-rose-950/60 text-rose-100" : "bg-zinc-800/80 text-zinc-500"
      }`}>{count}</span>
    </button>
  );
}

export default function CatalogPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [releases, setReleases] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showSuggestFromSearch, setShowSuggestFromSearch] = useState(false);
  const [showAddRelease, setShowAddRelease] = useState(false);
  // When a Quick Scan finds no catalog match, bubble the AI-identified
  // payload + photo up here and open the Add Custom Item dialog with both
  // pre-populated. State is `null` when no handoff is in flight.
  const [scanCustomHandoff, setScanCustomHandoff] = useState(null);

  const page = parseInt(searchParams.get("page") || "1");
  const search = searchParams.get("search") || "";
  const brand = searchParams.get("brand") || "";
  const series = searchParams.get("series") || "";
  const chassis = searchParams.get("chassis") || "";
  const vehicle = searchParams.get("vehicle") || "";
  const rarity = searchParams.get("rarity") || "";
  const variant_type = searchParams.get("variant_type") || "";
  const chase_only = searchParams.get("chase_only") === "true";
  const blkltd_only = searchParams.get("blkltd_only") === "true";
  const sort = searchParams.get("sort") || "newest";
  const personalize = searchParams.get("personalize") === "true";

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", "24");
    params.set("sort", sort);
    if (search) params.set("search", search);
    if (brand) params.set("brand", brand);
    if (series) params.set("series", series);
    if (chassis) params.set("chassis", chassis);
    if (vehicle) params.set("vehicle", vehicle);
    if (rarity) params.set("rarity", rarity);
    if (variant_type) params.set("variant_type", variant_type);
    if (chase_only) params.set("chase_only", "true");
    if (blkltd_only) params.set("blkltd_only", "true");
    if (personalize) params.set("personalize", "true");
    return params.toString();
  }, [page, search, brand, series, chassis, vehicle, rarity, variant_type, chase_only, blkltd_only, sort, personalize]);

  const fetchReleases = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/releases?${queryString}`);
      setReleases(data.releases);
      setTotal(data.total);
      setPages(data.pages);
    } catch { toast.error("Failed to load releases"); }
    finally { setLoading(false); }
  }, [queryString]);

  useEffect(() => { fetchReleases(); }, [fetchReleases]);

  const fetchFilters = useCallback(async () => {
    try {
      const { data } = await api.get("/releases/filters/options");
      setFilters(data);
    } catch (err) { console.error("Failed to load filter options:", err); }
  }, []);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  // Top brands for the quick-tab strip — sorted by release_count from the
  // public Brand Index. Only fetched once at mount; brand counts change
  // infrequently and re-fetching on every filter change wastes bandwidth.
  const [topBrands, setTopBrands] = useState([]);
  useEffect(() => {
    api.get("/brands")
      .then((r) => {
        const active = (r.data?.brands || []).filter((b) => b.release_count > 0);
        active.sort((a, b) => b.release_count - a.release_count);
        // Keep top 5 — anything beyond that lives in the Filters drawer.
        setTopBrands(active.slice(0, 5));
      })
      .catch(() => setTopBrands([]));
  }, []);

  const updateFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    if (key !== "page") p.set("page", "1");
    setSearchParams(p);
  };

  const hasActiveFilters = brand || series || chassis || vehicle || rarity || variant_type || chase_only || blkltd_only || search;

  const handleAddCollection = async (release) => {
    try {
      await api.post("/collection", { release_id: release.release_id });
      toast.success("Added to collection!");
      fetchReleases();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleAddWishlist = async (release) => {
    try {
      await api.post("/wishlist", { release_id: release.release_id });
      toast.success("Added to wishlist!");
      fetchReleases();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="catalog-page" className="p-4 sm:p-6 lg:p-8 space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-100 tracking-tight">Release Catalog</h1>
          <p className="text-zinc-500 mt-1 text-sm">{total} releases in database</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <QuickScanDialog
            onCollectionChange={fetchReleases}
            onCatalogChange={fetchReleases}
            onAddAsCustom={(prefill, photo) => setScanCustomHandoff({ prefill, photo })}
          />
          <BarcodeScanDialog
            onCollectionChange={fetchReleases}
            onCatalogChange={fetchReleases}
            onAddAsCustom={(prefill, photo) => setScanCustomHandoff({ prefill, photo })}
          />
          <SuggestReleaseDialog />
          {isAdmin && (
            <Button data-testid="add-release-btn" onClick={() => setShowAddRelease(true)} className="bg-rose-600 hover:bg-rose-500 text-white">
              <Plus className="w-4 h-4 mr-1" /> Add Release
            </Button>
          )}
        </div>
      </div>

      <CatalogSearchBar search={search} sort={sort} hasActiveFilters={hasActiveFilters} showFilters={showFilters} onFilterChange={updateFilter} onToggleFilters={() => setShowFilters(!showFilters)} />

      {/* Brand quick-switch strip. Tabs are derived live from the
          /api/brands endpoint (top 5 by release_count) so the bar stays
          relevant as new brands are imported. Hidden when only one brand
          has releases (no point showing a single tab). */}
      {topBrands.length > 1 && (
        <div data-testid="catalog-brand-tabs" className="flex items-center gap-1.5 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
          <BrandTab
            active={!brand}
            onClick={() => updateFilter("brand", "")}
            label="All brands"
            count={topBrands.reduce((s, b) => s + b.release_count, 0)}
            testId="brand-tab-all"
          />
          {topBrands.map((b) => (
            <BrandTab
              key={b.canonical}
              active={brand === b.canonical}
              onClick={() => updateFilter("brand", b.canonical)}
              label={b.canonical}
              count={b.release_count}
              tier={b.tier}
              testId={`brand-tab-${b.slug}`}
            />
          ))}
        </div>
      )}

      {user && (
        <div className="flex items-center justify-between bg-gradient-to-r from-amber-500/10 to-fuchsia-500/10 ring-1 ring-amber-400/20 rounded-xl px-4 py-2.5">
          <div className="flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="text-zinc-300">
              {personalize ? "Personalized for your archetype" : "Try smart recommendations"}
            </span>
          </div>
          <button
            data-testid="catalog-personalize-toggle"
            onClick={() => updateFilter("personalize", personalize ? null : "true")}
            className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
              personalize
                ? "bg-amber-400 text-black hover:bg-amber-300"
                : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"
            }`}
          >
            {personalize ? "On" : "For You"}
          </button>
        </div>
      )}

      {showFilters && (
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/5 rounded-xl p-4 space-y-3 animate-fadeInUp">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-300">Filters</h3>
            {hasActiveFilters && (
              <button data-testid="clear-filters" onClick={() => setSearchParams({})} className="text-xs text-rose-500 hover:text-rose-400 flex items-center gap-1">
                <X className="w-3 h-3" /> Clear all
              </button>
            )}
          </div>
          <CatalogFilters filters={filters} vehicle={vehicle} series={series} variant_type={variant_type} rarity={rarity} chase_only={chase_only} blkltd_only={blkltd_only} sort={sort} onFilterChange={updateFilter} />
        </div>
      )}

      {loading ? <CatalogSkeleton /> : releases.length === 0 ? (
        <div data-testid="catalog-empty-state" className="text-center py-16 max-w-md mx-auto">
          <p className="text-zinc-500 text-lg">No releases found</p>
          {search ? (
            <>
              <p className="text-zinc-600 text-sm mt-1">Couldn&apos;t find anything matching &ldquo;{search}&rdquo;.</p>
              <div className="mt-6 rounded-2xl bg-gradient-to-br from-zinc-900/60 to-purple-950/15 border border-dashed border-purple-500/25 p-5 text-left">
                <p className="text-sm text-zinc-200 font-medium">Looks like a missing release?</p>
                <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                  Suggest it to the catalog — once an admin approves, your future scans match instantly and you get XP.
                </p>
                <Button
                  data-testid="catalog-suggest-from-search"
                  size="sm"
                  onClick={() => setShowSuggestFromSearch(true)}
                  className="mt-3 bg-purple-600 hover:bg-purple-500 text-white text-xs"
                >
                  Suggest &ldquo;{search.length > 32 ? search.slice(0, 32) + "…" : search}&rdquo;
                </Button>
              </div>
            </>
          ) : (
            <p className="text-zinc-600 text-sm mt-1">Try adjusting your filters</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {releases.map((r) => (
            <ReleaseCard key={r.release_id} release={r} onAddCollection={handleAddCollection} onAddWishlist={handleAddWishlist} onCardClick={(id) => navigate(`/catalog/${id}`)} />
          ))}
        </div>
      )}

      <CatalogPagination page={page} pages={pages} onFilterChange={updateFilter} />

      {/* Controlled SuggestReleaseDialog opened from the empty-state CTA */}
      <SuggestReleaseDialog
        open={showSuggestFromSearch}
        onOpenChange={setShowSuggestFromSearch}
        prefill={{ release_name: search }}
        onSubmitted={() => setShowSuggestFromSearch(false)}
      />

      {isAdmin && (
        <AdminReleaseForm open={showAddRelease} onOpenChange={setShowAddRelease} onSuccess={fetchReleases} />
      )}

      {/* Controlled Add Custom Item dialog mounted only when a Quick Scan
          handed off AI-identified data. Closing nulls the state out. */}
      {scanCustomHandoff && (
        <AddCustomItemDialog
          open={!!scanCustomHandoff}
          onOpenChange={(o) => {
            if (!o) setScanCustomHandoff(null);
          }}
          prefillData={scanCustomHandoff.prefill}
          prefillPhoto={scanCustomHandoff.photo}
          onCreated={() => {
            setScanCustomHandoff(null);
            fetchReleases();
          }}
        />
      )}
    </div>
  );
}
